import {
  AREA_UNITS,
  OPERATIONS,
  PROPERTY_FIELD_LABELS,
  PROPERTY_STATUSES,
  PROPERTY_TYPES,
  WEB_PUBLISHABLE_STATUSES,
  formatDate,
  formatDateTime,
  formatMoney,
  type PropertyField,
  type PropertyStatus,
  WEB_LOCATION_MODES,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, Globe, Pencil, Rotate3d, Star, Trash2, Video } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { ActivitiesCard } from '../../components/activities/activities-card.tsx';
import { ListingBadge, StatusBadge } from '../../components/properties/badges.tsx';
import { MediaManager } from '../../components/properties/media-manager.tsx';
import { ShareLinksCard } from '../../components/properties/share-links-card.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { formatDay } from '../../lib/privacy-api.ts';
import {
  priceLabel,
  propertyKeys,
  useProperty,
  type PropertyDetail,
} from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

// Static classes (no inline style: strict CSP), in steps of 10 %.
const PROGRESS_WIDTHS = [
  'w-0',
  'w-[10%]',
  'w-[20%]',
  'w-[30%]',
  'w-[40%]',
  'w-[50%]',
  'w-[60%]',
  'w-[70%]',
  'w-[80%]',
  'w-[90%]',
  'w-full',
];

export function PropertyDetailPage() {
  const { id = '' } = useParams();
  const property = useProperty(id);
  if (property.isPending) return <Loading />;
  if (property.error) {
    return (property.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(property.error)}</Alert>
    );
  }
  return <PropertyView property={property.data} />;
}

function useRefresh() {
  const client = useQueryClient();
  return (saved: PropertyDetail) => {
    client.setQueryData(propertyKeys.detail(saved.id), saved);
    return client.invalidateQueries({ queryKey: propertyKeys.all });
  };
}

function PropertyView({ property }: { property: PropertyDetail }) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const notify = useToast();
  const refresh = useRefresh();
  const [changingStatus, setChangingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const p = property;
  const money = (n: number) => formatMoney(n);
  const fields = PROPERTY_TYPES[p.type].fields as readonly PropertyField[];
  const unitOf = (field: 'builtArea' | 'lotArea') =>
    AREA_UNITS[field === 'lotArea' ? p.lotAreaUnit : p.builtAreaUnit];
  const canPublishNow = WEB_PUBLISHABLE_STATUSES.includes(p.status) && p.completeness.publishable;

  const toggleWeb = async (featured?: boolean) => {
    setPublishing(true);
    try {
      const published = featured === undefined ? !p.webPublished : true;
      const saved = await api<PropertyDetail>(`/properties/${p.id}/web`, {
        method: 'POST',
        body: { published, ...(featured === undefined ? {} : { featured }) },
      });
      await refresh(saved);
      notify(
        featured !== undefined
          ? featured
            ? 'Destacada en la portada del sitio web.'
            : 'Ya no está destacada.'
          : saved.webPublished
            ? 'Publicada en el sitio web.'
            : 'Retirada del sitio web.',
        'success',
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/propiedades" className="text-sm text-primary hover:underline">
          ← Propiedades
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">{p.title}</h1>
            <p className="text-lg font-semibold tabular-nums">{priceLabel(p, money)}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-sm text-muted-foreground">{p.code}</span>
              <StatusBadge status={p.status} />
              <Badge>{PROPERTY_TYPES[p.type].label}</Badge>
              <Badge>{OPERATIONS[p.operation]}</Badge>
              {p.exclusive && <Badge tone="info">Exclusiva</Badge>}
              <ListingBadge state={p.listingState} />
              {p.webPublished && <Badge tone="success">En la web</Badge>}
              {p.webFeatured && <Badge tone="info">Destacada</Badge>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.can.edit && (
              <Link
                to={`/propiedades/${p.id}/editar`}
                className={buttonVariants({ variant: 'secondary' })}
              >
                <Pencil /> Editar
              </Link>
            )}
            {p.can.edit && (
              <Button
                variant="secondary"
                onClick={() => {
                  setChangingStatus(true);
                }}
              >
                Cambiar estado
              </Button>
            )}
            {p.can.delete && (
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleting(true);
                }}
              >
                <Trash2 /> Eliminar
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium">Ficha completa al {p.completeness.percent} %</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label="Completitud de la ficha"
              aria-valuenow={p.completeness.percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={cn(
                  'h-full rounded-full bg-primary',
                  PROGRESS_WIDTHS[Math.round(p.completeness.percent / 10)],
                )}
              />
            </div>
            {p.completeness.missing.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Falta: {p.completeness.missing.join(', ').toLowerCase()}.
              </p>
            )}
          </div>
          {p.can.publish && (
            <div className="space-y-1 sm:text-right">
              <Button
                variant={p.webPublished ? 'secondary' : 'brand'}
                loading={publishing}
                disabled={!p.webPublished && !canPublishNow}
                onClick={() => void toggleWeb()}
              >
                <Globe /> {p.webPublished ? 'Retirar de la web' : 'Publicar en la web'}
              </Button>
              {p.webPublished && (
                <Button
                  variant="ghost"
                  loading={publishing}
                  aria-pressed={p.webFeatured}
                  onClick={() => void toggleWeb(!p.webFeatured)}
                >
                  <Star /> {p.webFeatured ? 'Quitar de destacadas' : 'Destacar en la web'}
                </Button>
              )}
              {!p.webPublished && !canPublishNow && (
                <p className="text-xs text-muted-foreground">
                  {WEB_PUBLISHABLE_STATUSES.includes(p.status)
                    ? `Para publicar falta: ${p.completeness.blocking.join(', ').toLowerCase()}.`
                    : 'Solo se publican propiedades disponibles o reservadas.'}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      <MediaManager property={p} />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="space-y-3">
          <h2 className="font-semibold">Ubicación</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Distrito</dt>
            <dd>
              {p.district.name} · {p.district.municipality}
            </dd>
            <dt className="text-muted-foreground">Zona</dt>
            <dd>{p.neighborhood ?? '—'}</dd>
            <dt className="text-muted-foreground">Código catastral</dt>
            <dd>{p.cadastralCode ?? '—'}</dd>
          </dl>
          <PrivateLocation property={p} />
        </Card>

        <Card className="space-y-3">
          <h2 className="font-semibold">Características</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {fields.map((field) => {
              const value = p.characteristics[field];
              const label = PROPERTY_FIELD_LABELS[field];
              return (
                <div key={field} className="contents">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd>
                    {value === null
                      ? '—'
                      : field === 'builtArea' || field === 'lotArea'
                        ? `${String(value)} ${unitOf(field)}`
                        : field === 'maintenanceFee'
                          ? money(value)
                          : String(value)}
                  </dd>
                </div>
              );
            })}
          </dl>
        </Card>

        <Card className="space-y-2 lg:col-span-2">
          <h2 className="font-semibold">Descripción</h2>
          <p className="text-sm whitespace-pre-line">{p.description ?? 'Sin descripción.'}</p>
          {p.titleEn ? (
            <div className="space-y-1 border-t border-border pt-2" lang="en">
              <h3 className="text-sm font-medium">{p.titleEn}</h3>
              {p.descriptionEn && <p className="text-sm whitespace-pre-line">{p.descriptionEn}</p>}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Sin versión en inglés: no aparece en el sitio en inglés.
            </p>
          )}
          {(p.videoUrl ?? p.virtualTourUrl) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {p.videoUrl && (
                <a
                  href={p.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonVariants({ size: 'sm', variant: 'secondary' })}
                >
                  <Video /> Ver video
                </a>
              )}
              {p.virtualTourUrl && (
                <a
                  href={p.virtualTourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonVariants({ size: 'sm', variant: 'secondary' })}
                >
                  <Rotate3d /> Recorrido virtual
                </a>
              )}
            </div>
          )}
        </Card>

        <div className="lg:col-span-2">
          <ShareLinksCard property={p} />
        </div>

        <div className="lg:col-span-2">
          <ActivitiesCard
            link="property"
            id={p.id}
            draft={{ kind: 'visit', property: { id: p.id, label: `${p.code} · ${p.title}` } }}
          />
        </div>

        <Card className="space-y-3">
          <h2 className="font-semibold">Propietarios</h2>
          {p.ownersCount === 0 ? (
            <Alert tone="warning" title="Sin propietario">
              {p.external ? 'Se importó de RECONNECT sin propietario. ' : ''}
              Agrégalo al editar la propiedad; mientras tanto no se puede publicar.
            </Alert>
          ) : p.owners === null ? (
            <p className="text-sm text-muted-foreground">
              {p.ownersCount === 1
                ? '1 propietario registrado'
                : `${String(p.ownersCount)} propietarios registrados`}
              ; tu rol no permite ver quiénes son.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {p.owners.map((o) => (
                <li key={o.contactId} className="flex justify-between gap-3">
                  <Link to={`/contactos/${o.contactId}`} className="text-primary hover:underline">
                    {o.name}
                  </Link>
                  {o.sharePct !== null && <span>{o.sharePct} %</span>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="space-y-3">
          <h2 className="font-semibold">Captación</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Responsable</dt>
            <dd>{p.assignedTo.name}</dd>
            <dt className="text-muted-foreground">Captador</dt>
            <dd>{p.capturedBy.name}</dd>
            <dt className="text-muted-foreground">Acuerdo</dt>
            <dd>
              {p.exclusive ? 'Exclusiva' : 'Sin exclusiva'}
              {p.listingStartsOn && ` · desde ${formatDay(p.listingStartsOn)}`}
              {p.listingEndsOn && ` · vence el ${formatDay(p.listingEndsOn)}`}
            </dd>
            {p.closed && (
              <>
                <dt className="text-muted-foreground">Cierre</dt>
                <dd>
                  {[
                    p.closed.price !== null && money(p.closed.price),
                    p.closed.on && formatDay(p.closed.on),
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Sin datos'}
                </dd>
              </>
            )}
            {p.external && (
              <>
                <dt className="text-muted-foreground">RECONNECT</dt>
                <dd>
                  Listing ID <span className="font-mono">{p.external.id}</span>
                  {p.external.type ? ` · ${p.external.type}` : ''}
                </dd>
              </>
            )}
            <dt className="text-muted-foreground">En el mercado</dt>
            <dd>
              {p.daysOnMarket === null
                ? 'Todavía no'
                : p.daysOnMarket === 1
                  ? '1 día'
                  : `${String(p.daysOnMarket)} días`}
            </dd>
            <dt className="text-muted-foreground">Alta</dt>
            <dd>
              {formatDate(p.createdAt, tz)}
              {p.createdBy ? ` · ${p.createdBy}` : ''}
            </dd>
          </dl>
        </Card>

        <Card className="space-y-3">
          <h2 className="font-semibold">Comisión acordada</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {p.commission.salePct !== null && (
              <>
                <dt className="text-muted-foreground">Venta</dt>
                <dd>{p.commission.salePct} %</dd>
              </>
            )}
            {p.commission.rentMonths !== null && (
              <>
                <dt className="text-muted-foreground">Alquiler</dt>
                <dd>
                  {p.commission.rentMonths} {p.commission.rentMonths === 1 ? 'mes' : 'meses'}
                </dd>
              </>
            )}
            <dt className="text-muted-foreground">A compartir con la Red</dt>
            <dd>
              {p.commission.networkSharePct === null
                ? '—'
                : `${String(p.commission.networkSharePct)} %`}
            </dd>
          </dl>
          {p.commission.conditions && (
            <p className="text-sm whitespace-pre-line text-muted-foreground">
              {p.commission.conditions}
            </p>
          )}
        </Card>

        <Card className="space-y-3">
          <h2 className="font-semibold">Datos privados</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Llaves</dt>
            <dd>
              {p.keysCount === null ? '—' : String(p.keysCount)}
              {p.keysLocation ? ` · ${p.keysLocation}` : ''}
            </dd>
          </dl>
          <p className="text-sm whitespace-pre-line text-muted-foreground">
            {p.privateNotes ?? 'Sin notas privadas.'}
          </p>
        </Card>

        <Card className="space-y-3 lg:col-span-2">
          <h2 className="font-semibold">Historial</h2>
          <ul className="space-y-2 text-sm">
            {[
              ...p.statusHistory.map((h) => ({
                at: h.at,
                text: h.from
                  ? `Estado: ${PROPERTY_STATUSES[h.from]} → ${PROPERTY_STATUSES[h.to]}`
                  : 'Registrada como borrador',
                note: h.note,
                by: h.by,
              })),
              ...p.priceHistory.map((h) => ({
                at: h.at,
                text: `Precio de ${h.kind === 'sale' ? 'venta' : 'alquiler'}: ${
                  h.from === null ? '—' : money(h.from)
                } → ${h.to === null ? '—' : money(h.to)}`,
                note: null,
                by: h.by,
              })),
            ]
              .sort((a, b) => b.at.localeCompare(a.at))
              .map((entry, i) => (
                <li key={`${entry.at}-${String(i)}`} className="flex flex-col sm:flex-row sm:gap-3">
                  <span className="shrink-0 text-muted-foreground tabular-nums">
                    {formatDateTime(entry.at, tz)}
                  </span>
                  <span>
                    {entry.text}
                    {entry.note ? ` — ${entry.note}` : ''}
                    {entry.by ? ` · ${entry.by}` : ''}
                  </span>
                </li>
              ))}
          </ul>
        </Card>
      </div>

      {changingStatus && (
        <StatusDialog
          property={p}
          onClose={() => {
            setChangingStatus(false);
          }}
          onSaved={async (saved) => {
            await refresh(saved);
            setChangingStatus(false);
            notify(`Estado: ${PROPERTY_STATUSES[saved.status]}.`, 'success');
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          property={p}
          onClose={() => {
            setDeleting(false);
          }}
        />
      )}
    </div>
  );
}

/** Exact address and map point: hidden until revealed (audited). */
function PrivateLocation({ property }: { property: PropertyDetail }) {
  const notify = useToast();
  const [values, setValues] = useState<{
    address: string | null;
    location: { lat: number; lng: number } | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const nothing = !property.hasAddress && !property.hasLocation;
  const reveal = async () => {
    setBusy(true);
    try {
      setValues(await api(`/properties/${property.id}/reveal`, { method: 'POST' }));
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium">Dirección exacta y mapa (privados)</h3>
        {!nothing && property.can.revealSensitive && !values && (
          <Button size="sm" variant="secondary" loading={busy} onClick={() => void reveal()}>
            <Eye /> Mostrar
          </Button>
        )}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Dirección</dt>
        <dd>
          {values?.address ?? (property.hasAddress ? 'Registrada (oculta)' : 'No registrada')}
        </dd>
        <dt className="text-muted-foreground">Coordenadas</dt>
        <dd>
          {values?.location
            ? `${String(values.location.lat)}, ${String(values.location.lng)}`
            : property.hasLocation
              ? 'Registradas (ocultas)'
              : 'No registradas'}
        </dd>
        <dt className="text-muted-foreground">Mapa del sitio web</dt>
        <dd>
          {property.hasLocation
            ? WEB_LOCATION_MODES[property.webLocationMode]
            : 'Sin mapa (no hay coordenadas)'}
        </dd>
      </dl>
      {values && (
        <p className="text-xs text-muted-foreground">
          Esta consulta quedó registrada en la bitácora.
        </p>
      )}
      {!nothing && !property.can.revealSensitive && (
        <p className="text-xs text-muted-foreground">Tu rol no permite ver estos datos.</p>
      )}
    </div>
  );
}

function StatusDialog({
  property,
  onClose,
  onSaved,
}: {
  property: PropertyDetail;
  onClose: () => void;
  onSaved: (saved: PropertyDetail) => Promise<void>;
}) {
  const notify = useToast();
  const [status, setStatus] = useState<PropertyStatus>(property.status);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const saved = await api<PropertyDetail>(`/properties/${property.id}/status`, {
        method: 'POST',
        body: { status, ...(note.trim() ? { note } : {}) },
      });
      await onSaved(saved);
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };
  const leavesWeb = property.webPublished && !WEB_PUBLISHABLE_STATUSES.includes(status);
  return (
    <Dialog open title="Cambiar estado" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Nuevo estado">
          {(a11y) => (
            <Select
              {...a11y}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as PropertyStatus);
              }}
            >
              {(Object.entries(PROPERTY_STATUSES) as [PropertyStatus, string][]).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ),
              )}
            </Select>
          )}
        </Field>
        <Field label="Nota (opcional)">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={500}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
        </Field>
        {leavesWeb && (
          <Alert tone="warning">Con este estado la propiedad se retira del sitio web.</Alert>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="brand"
            loading={busy}
            disabled={status === property.status}
          >
            Guardar estado
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeleteDialog({ property, onClose }: { property: PropertyDetail; onClose: () => void }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/properties/${property.id}`, { method: 'DELETE' });
      await client.invalidateQueries({ queryKey: propertyKeys.all });
      notify(`«${property.title}» se envió a la papelera.`, 'success');
      await navigate('/propiedades');
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title="Eliminar propiedad"
      description="Va a la papelera: se puede restaurar durante 30 días. Si está en la web, se retira."
      onClose={onClose}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={busy} onClick={() => void remove()}>
          Enviar a la papelera
        </Button>
      </div>
    </Dialog>
  );
}
